// recorderutils.js
import { Plugin } from '@ckeditor/ckeditor5-core';
import { findOptimalInsertionRange, isWidget, toWidget } from '@ckeditor/ckeditor5-widget';

export default class RecorderUtils extends Plugin {
    static get pluginName() {
        return 'RecorderUtils';
    }

    // --- Identificación ---
    isRecorder( modelElement ) {
        return this.isBlockRecorder( modelElement );
    }

    isBlockRecorder( modelElement ) {
        return !!modelElement && modelElement.is( 'element', 'recorderBlock' );
    }

    isBlockRecorderView( element ) {
        return !!element && element.is( 'element', 'figure' ) && element.hasClass( 'recorder' );
    }

    // --- Inserción ---
    insertRecorder( attributes = {}, selectable = null ) {
        const editor = this.editor;
        const model = editor.model;
        const selection = model.document.selection;

        attributes = {
            ...Object.fromEntries( selection.getAttributes() ),
            ...attributes
        };

        for ( const attributeName in attributes ) {
            if ( !model.schema.checkAttribute( 'recorderBlock', attributeName ) ) {
                delete attributes[ attributeName ];
            }
        }

        return model.change( writer => {
            const recorderElement = writer.createElement( 'recorderBlock', attributes );

            model.insertObject( recorderElement, selectable, null, {
                setSelection: 'on',
                findOptimalPosition: !selectable ? 'auto' : undefined
            } );

            return recorderElement.parent ? recorderElement : null;
        } );
    }

    // --- Selección ---
    getClosestSelectedRecorderWidget( selection ) {
        const selectionPosition = selection.getFirstPosition();
        if ( !selectionPosition ) return null;

        const viewElement = selection.getSelectedElement();
        if ( viewElement && this.isRecorderWidget( viewElement ) ) {
            return viewElement;
        }

        let parent = selectionPosition.parent;
        while ( parent ) {
            if ( parent.is( 'element' ) && this.isRecorderWidget( parent ) ) {
                return parent;
            }
            parent = parent.parent;
        }
        return null;
    }

    getClosestSelectedRecorderElement( selection ) {
        const selectedElement = selection.getSelectedElement();
        return this.isRecorder( selectedElement )
            ? selectedElement
            : selection.getFirstPosition().findAncestor( 'recorderBlock' );
    }

    // --- Validación ---
    isRecorderAllowed() {
        const model = this.editor.model;
        const selection = model.document.selection;

        return isRecorderAllowedInParent( this.editor, selection ) && isNotInsideRecorder( selection );
    }

    // --- Widgets ---
    toRecorderWidget( viewElement, writer ) {
        writer.setCustomProperty( 'recorder', true, viewElement );
        return toWidget( viewElement, writer );
    }

    isRecorderWidget( viewElement ) {
        return !!viewElement.getCustomProperty( 'recorder' ) && isWidget( viewElement );
    }

    // --- Vista ---
    findViewRecorderElement( figureView ) {
        const editingView = this.editor.editing.view;
        for ( const { item } of editingView.createRangeIn( figureView ) ) {
            if ( item.is( 'element', 'audio' ) || item.is( 'element', 'video' ) ) {
                return item;
            }
        }
        return figureView;
    }
}

// =============================
// Helpers privados
// =============================
function isRecorderAllowedInParent( editor, selection ) {
    const parent = getInsertRecorderParent( selection, editor.model );
    return editor.model.schema.checkChild( parent, 'recorderBlock' );
}

function isNotInsideRecorder( selection ) {
    return [ ...selection.focus.getAncestors() ].every(
        ancestor => !ancestor.is( 'element', 'recorderBlock' )
    );
}

function getInsertRecorderParent( selection, model ) {
    const insertionRange = findOptimalInsertionRange( selection, model );
    const parent = insertionRange.start.parent;

    if ( parent.isEmpty && !parent.is( 'element', '$root' ) ) {
        return parent.parent;
    }

    return parent;
}
